/* =====================================================================
 * Dashboard chart palette — validated, not hand-picked.
 *
 * Every value below was produced by snapping candidates to a passing
 * step with the dataviz validator, then recorded here. Do not eyeball a
 * replacement: re-run the validator and paste the result.
 *
 *   categorical (2 series, adjacent pairs)
 *     light  #0055FF,#10a37f              → ALL PASS (CVD ΔE 27.7, normal 31.2)
 *     dark   #3D80FF,#199e79              → ALL PASS (CVD ΔE 24.1 normal)
 *
 *   sequential ramp (heatmap, --ordinal)
 *     light  #8AB1FF,#4D89FF,#0F5CF5,#0038A8
 *            → monotone L, ΔL ≥ 0.06, light end 2.08:1, hue spread 1°
 *     dark   #2B50A0,#2A66D6,#3D80FF,#8FB3FF
 *            → monotone L, ΔL ≥ 0.06, light end 2.11:1, hue spread 3°
 *
 * Why these hues: slot 1 is the tenant's brand (SOLA blue #0055FF) so
 * the dashboard reads as part of the product rather than a bolted-on
 * report; slot 2 is the existing `feedback.success` green, already in
 * the palette tokens. The dark steps are re-picked rather than
 * auto-lightened — the dark lightness band is narrower (0.48–0.67) and
 * a naive flip fails it.
 *
 * The ramp runs light→dark in light mode and dark→light in dark mode:
 * "more" must always mean "further from the surface".
 * ===================================================================== */

export type ChartMode = 'light' | 'dark';

/** Series identity. Fixed order — never cycled, never sorted by value. */
export const CATEGORICAL: Record<ChartMode, readonly [string, string]> = {
  light: ['#0055FF', '#10a37f'],
  dark: ['#3D80FF', '#199e79'],
} as const;

/** Magnitude ramp for the contribution heatmap, quietest → loudest. */
export const SEQUENTIAL: Record<ChartMode, readonly string[]> = {
  light: ['#8AB1FF', '#4D89FF', '#0F5CF5', '#0038A8'],
  dark: ['#2B50A0', '#2A66D6', '#3D80FF', '#8FB3FF'],
} as const;

/**
 * The "no activity" cell. Deliberately a surface tint rather than the
 * ramp's lightest step — an empty day must not read as a small amount.
 */
export const EMPTY_CELL: Record<ChartMode, string> = {
  light: 'rgba(13, 13, 13, 0.06)',
  dark: 'rgba(236, 236, 236, 0.08)',
};

/**
 * Bucket a count into a ramp index.
 *
 * Quartiles of the observed peak rather than fixed thresholds: usage
 * varies by orders of magnitude between a trial user and a heavy one,
 * and a fixed scale would render one of them as a flat grid.
 */
export function rampIndex(count: number, peak: number): number {
  if (count <= 0) return -1;
  if (peak <= 0) return 0;
  const ratio = count / peak;
  if (ratio <= 0.25) return 0;
  if (ratio <= 0.5) return 1;
  if (ratio <= 0.75) return 2;
  return 3;
}

export function cellColor(count: number, peak: number, mode: ChartMode): string {
  const index = rampIndex(count, peak);
  return index < 0 ? EMPTY_CELL[mode] : SEQUENTIAL[mode][index];
}

/**
 * Series colour by *identity*, never by rank — filtering the list must
 * not repaint the survivors.
 */
export function seriesColor(
  workspaceType: string,
  order: readonly string[],
  mode: ChartMode,
): string {
  const index = order.indexOf(workspaceType);
  const palette = CATEGORICAL[mode];
  return palette[Math.max(0, index) % palette.length];
}
